import { Button } from '@chakra-ui/react';
import { useKeyPress } from '@common/hooks/useKeyPress';
import React, { CSSProperties, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';

// REF: Examples from author of signature_pad
// https://github.com/szimek/signature_pad/blob/gh-pages/js/app.js

function resizeCanvas(signaturePad: SignaturePad, canvas: HTMLCanvasElement) {
  // normalize devicePixelRatio across browsers
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  // This part causes the canvas to be cleared
  canvas.width = canvas.offsetWidth * ratio;
  canvas.height = canvas.offsetHeight * ratio;
  canvas?.getContext('2d')?.scale(ratio, ratio);
  // clear manually to make sure the visual and data state is cleared
  signaturePad.clear();
}

const SignatureStyle = (): Record<string, CSSProperties> => ({
  canvas: {
    height: 200,
    width: '100%',
  },
  canvasWrapper: {
    borderBottom: '2px dashed rgba(0,0,0,0.15)',
    bottom: '15%',
    left: '10%',
    position: 'absolute',
    width: '80%',
  },
  clearButton: {
    position: 'absolute',
    right: 20,
    top: 20,
    zIndex: 1,
  },
  signatureBoxWrapper: {
    border: `1px solid rgba(0,0,0,0.15)`,
    borderRadius: 10,
    display: 'inline-block',
    height: 200,
    position: 'relative',
    width: '100%',
  },
  wrapper: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    justifyContent: 'flex-end',
    position: 'relative',
    width: '100%',
  },
});

type SignatureBoxProps = {
  onClear: () => void;
  onSave: (data: string, blob: Blob) => void;
};

export const SignatureBox: React.FC<SignatureBoxProps> = ({ onSave, onClear }) => {
  const [hasSignature, setHasSignature] = useState<boolean>(false);
  const signaturePad = React.useRef<SignaturePad>();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const style = SignatureStyle();
  const onDeleteKey = useKeyPress('Escape');

  async function handleSignatureSave() {
    if (!signaturePad.current) return;
    const imageData = await fetch(signaturePad.current.toDataURL('image/png'));
    const blobData = await imageData.blob();
    const dataUrl = signaturePad.current.toDataURL('image/png');
    setHasSignature(true);
    onSave(dataUrl, blobData);
  }

  function handleClear() {
    if (!signaturePad.current) return;
    signaturePad.current.clear();
    setHasSignature(false);
    onClear();
  }

  // clear on escape key
  useEffect(() => {
    if (onDeleteKey) handleClear();
  }, [onDeleteKey]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'transparent',
      dotSize: 1,
      maxWidth: 2,
      minWidth: 2,
      penColor: 'rgb(0, 0, 0)',
    });
    pad.on();
    pad.addEventListener('endStroke', handleSignatureSave);
    window.addEventListener('resize', () => resizeCanvas(pad, canvas));
    resizeCanvas(pad, canvas);
    signaturePad.current = pad;
    return () => {
      pad.off();
      window.removeEventListener('resize', () => resizeCanvas(pad, canvas));
      pad.removeEventListener('endStroke', handleSignatureSave);
    };
  }, []);

  return (
    <div style={style.wrapper}>
      <div style={style.signatureBoxWrapper}>
        <div style={style.canvasWrapper}></div>
        <canvas ref={canvasRef} style={style.canvas} />
      </div>
      {hasSignature === true && (
        <Button variant={'link'} onClick={handleClear} style={style.clearButton}>
          Clear
        </Button>
      )}
    </div>
  );
};

// download
// dataURLToBlog
// clear button
// undo button
// save to png
// save to jpg
// save to svg

// function download(dataURL, filename) {
//   var blob = dataURLToBlob(dataURL);
//   var url = window.URL.createObjectURL(blob);

//   var a = document.createElement('a');
//   a.style = 'display: none';
//   a.href = url;
//   a.download = filename;

//   document.body.appendChild(a);
//   a.click();

//   window.URL.revokeObjectURL(url);
// }

// // One could simply use Canvas#toBlob method instead, but it's just to show
// // that it can be done using result of SignaturePad#toDataURL.
// function dataURLToBlob(dataURL) {
//   // Code taken from https://github.com/ebidel/filer.js
//   var parts = dataURL.split(';base64,');
//   var contentType = parts[0].split(':')[1];
//   var raw = window.atob(parts[1]);
//   var rawLength = raw.length;
//   var uInt8Array = new Uint8Array(rawLength);

//   for (var i = 0; i < rawLength; ++i) {
//     uInt8Array[i] = raw.charCodeAt(i);
//   }

//   return new Blob([uInt8Array], { type: contentType });
// }

// clearButton.addEventListener("click", function (event) {
//   signaturePad.clear();
// });

// undoButton.addEventListener("click", function (event) {
//   var data = signaturePad.toData();

//   if (data) {
//     data.pop(); // remove the last dot or line
//     signaturePad.fromData(data);
//   }
// });

// changeColorButton.addEventListener("click", function (event) {
//   var r = Math.round(Math.random() * 255);
//   var g = Math.round(Math.random() * 255);
//   var b = Math.round(Math.random() * 255);
//   var color = "rgb(" + r + "," + g + "," + b +")";

//   signaturePad.penColor = color;
// });

// savePNGButton.addEventListener("click", function (event) {
//   if (signaturePad.isEmpty()) {
//     alert("Please provide a signature first.");
//   } else {
//     var dataURL = signaturePad.toDataURL();
//     download(dataURL, "signature.png");
//   }
// });

// saveJPGButton.addEventListener("click", function (event) {
//   if (signaturePad.isEmpty()) {
//     alert("Please provide a signature first.");
//   } else {
//     var dataURL = signaturePad.toDataURL("image/jpeg");
//     download(dataURL, "signature.jpg");
//   }
// });

// saveSVGButton.addEventListener("click", function (event) {
//   if (signaturePad.isEmpty()) {
//     alert("Please provide a signature first.");
//   } else {
//     var dataURL = signaturePad.toDataURL('image/svg+xml');
//     download(dataURL, "signature.svg");
//   }
